/**
 * PolicyQAPanel — TN-IPDS Policy Q&A
 *
 * Natural language query interface for Tamil Nadu procurement policy.
 * Shows answers with citation chips linking to evidence_url.
 * Handles refusal strings and "no applicable policy" cases cleanly.
 *
 * API: POST /v1/tn-ipds/policy-qa
 */
import React, { useState, useRef } from 'react';
import { API_BASE } from '../../config/apiConfig';

// Suggested queries for quick access
const SUGGESTED_QUERIES = [
  'What is the minimum bid window for works contracts above ₹50 lakh?',
  'What are the MSME reservation rules for Tamil Nadu tenders?',
  'What is the maximum post-award variation allowed without re-tendering?',
  'What are the CM directive targets for MSME participation?',
  'What documents must a vendor submit for registration?',
  'What is the single-bid acceptance policy for emergency procurement?',
  'What are the payment release conditions after milestone acceptance?',
  'What is the bid security requirement for contracts above ₹1 crore?',
];

const REFUSAL_PHRASES = [
  'I cannot provide a legal conclusion',
  'No applicable policy found',
  'please consult legal',
  'please consult the Legal',
];

function isRefusal(text) {
  return REFUSAL_PHRASES.some(p => text.includes(p));
}

// Parse [policy_rule_id] citations from answer text
function parseCitations(text) {
  const matches = [...text.matchAll(/\[([A-Z0-9-]+)\]/g)];
  return [...new Set(matches.map(m => m[1]))];
}

const s = {
  container: { fontFamily: 'sans-serif', maxWidth: 800, margin: '0 auto', padding: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#666', marginBottom: 20 },
  inputRow: { display: 'flex', gap: 8, marginBottom: 16 },
  textarea: {
    flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, resize: 'vertical', minHeight: 60, fontFamily: 'inherit',
    outline: 'none',
  },
  btn: { padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnPrimary: { background: '#4f46e5', color: '#fff', alignSelf: 'flex-end' },
  suggestionsRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 },
  suggestionChip: {
    padding: '5px 12px', borderRadius: 20, border: '1px solid #d1d5db',
    background: '#f9fafb', cursor: 'pointer', fontSize: 12, color: '#374151',
    transition: 'background 0.1s',
  },
  answerCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  refusalCard: {
    background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10,
    padding: 20, marginBottom: 16,
  },
  noPolicy: {
    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
    padding: 20, marginBottom: 16,
  },
  citationChip: (hasUrl) => ({
    display: 'inline-block', background: '#eff6ff', color: '#1e40af',
    padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
    marginRight: 6, marginBottom: 4,
    cursor: hasUrl ? 'pointer' : 'default',
    textDecoration: hasUrl ? 'underline' : 'none',
  }),
  historyItem: {
    borderBottom: '1px solid #f3f4f6', paddingBottom: 12, marginBottom: 12,
  },
  langToggle: {
    display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center',
  },
  langBtn: (active) => ({
    padding: '4px 12px', borderRadius: 20, border: '1px solid #d1d5db',
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
    background: active ? '#4f46e5' : '#fff',
    color: active ? '#fff' : '#374151',
  }),
};

const PolicyQAPanel = ({ clientId, apiBase }) => {
  const base = apiBase || API_BASE || '/v1';
  const textareaRef = useRef();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [language, setLanguage] = useState('en');

  const handleQuery = async (q) => {
    const trimmed = (q || query).trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${base}/tn-ipds/policy-qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: trimmed,
          client_id: clientId,
          language,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Query failed (${res.status})`);
      }
      const data = await res.json();
      setHistory(prev => [{ query: trimmed, ...data, ts: new Date().toLocaleTimeString() }, ...prev]);
      setQuery('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleQuery();
    }
  };

  return (
    <div style={s.container}>
      <h2 style={s.title}>⚖️ Policy Q&A</h2>
      <p style={s.subtitle}>
        Ask questions about Tamil Nadu procurement policy. Answers cite specific
        policy rules. Legal conclusions are not provided — consult Legal/Vigilance
        for formal opinions.
      </p>

      {/* Language toggle */}
      <div style={s.langToggle}>
        <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>Language:</span>
        <button style={s.langBtn(language === 'en')} onClick={() => setLanguage('en')}>English</button>
        <button style={s.langBtn(language === 'ta')} onClick={() => setLanguage('ta')}>தமிழ் (Tamil)</button>
      </div>

      {/* Suggested queries */}
      <div style={s.suggestionsRow}>
        {SUGGESTED_QUERIES.map((sq, i) => (
          <span
            key={i}
            style={s.suggestionChip}
            onClick={() => { setQuery(sq); textareaRef.current?.focus(); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleQuery(sq)}
          >
            {sq.length > 55 ? sq.slice(0, 52) + '…' : sq}
          </span>
        ))}
      </div>

      {/* Query input */}
      <div style={s.inputRow}>
        <textarea
          ref={textareaRef}
          style={s.textarea}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a procurement policy question… (Ctrl+Enter to submit)"
          aria-label="Policy query"
          disabled={loading}
        />
        <button
          style={{ ...s.btn, ...s.btnPrimary, opacity: loading || !query.trim() ? 0.6 : 1 }}
          onClick={() => handleQuery()}
          disabled={loading || !query.trim()}
        >
          {loading ? '⏳' : '🔍 Ask'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 }} role="alert">
          ⚠ {error}
        </div>
      )}

      {/* Answer history */}
      {history.map((item, idx) => {
        const citations = parseCitations(item.answer || '');
        const refused = isRefusal(item.answer || '');
        const noPolicy = (item.answer || '').includes('No applicable policy found');

        const cardStyle = noPolicy ? s.noPolicy : refused ? s.refusalCard : s.answerCard;

        return (
          <div key={idx} style={cardStyle}>
            {/* Query */}
            <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
              <strong>Q:</strong> {item.query}
              <span style={{ float: 'right', fontSize: 11 }}>{item.ts}</span>
            </div>

            {/* Answer */}
            <div style={{ fontSize: 14, color: '#1a1a2e', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {refused && <span style={{ fontSize: 18, marginRight: 6 }}>⚠️</span>}
              {noPolicy && <span style={{ fontSize: 18, marginRight: 6 }}>ℹ️</span>}
              {item.answer}
            </div>

            {/* Tamil translation if available */}
            {item.answer_ta && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, fontSize: 13, color: '#374151' }}>
                <strong>தமிழ்:</strong> {item.answer_ta}
              </div>
            )}

            {/* Citation chips */}
            {citations.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>
                  POLICY CITATIONS
                </div>
                {citations.map((cit, ci) => {
                  const citData = item.citations?.find(c => c.rule_id === cit);
                  return (
                    <span
                      key={ci}
                      style={s.citationChip(!!citData?.evidence_url)}
                      onClick={() => citData?.evidence_url && window.open(citData.evidence_url, '_blank')}
                      title={citData?.rule_text || cit}
                    >
                      [{cit}]
                    </span>
                  );
                })}
              </div>
            )}

            {/* Precedence level */}
            {item.precedence_level && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
                Precedence: Level {item.precedence_level} ({item.jurisdiction_level || 'unknown'})
              </div>
            )}
          </div>
        );
      })}

      {history.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>
          Ask a policy question above to get started.
        </div>
      )}
    </div>
  );
};

export default PolicyQAPanel;
